/**
 * Cluster List Component
 * 展示所有配置的 K8s 集群，连通性自动批量检测（ArgoCD 风格）
 */

import React from 'react';
import {
  Table,
  TableColumn,
} from '@backstage/core-components';
import { Button, Chip, Box, Typography, IconButton, Tooltip, CircularProgress } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { ClusterListItem } from './types';
import { ConnectivityStatus } from './useClusterManagement';

interface ClusterListProps {
  clusters: ClusterListItem[];
  loading: boolean;
  onRefresh: () => void;
  onViewDetail: (clusterName: string) => void;
  connectivity: Record<string, ConnectivityStatus>;
  connectivityLoading: boolean;
}

export const ClusterList: React.FC<ClusterListProps> = ({
  clusters,
  loading,
  onRefresh,
  onViewDetail,
  connectivity,
  connectivityLoading,
}) => {
  const columns: TableColumn<ClusterListItem>[] = [
    {
      title: '集群名称',
      field: 'title',
      render: (row) => (
        <Box>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {row.title}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {row.name}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'URL',
      field: 'url',
      render: (row) => (
        <Typography variant="caption" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
          {row.url}
        </Typography>
      ),
    },
    {
      title: 'ServiceAccount',
      field: 'serviceAccount',
      render: (row) => (
        <Chip label={row.serviceAccount} size="small" variant="outlined" />
      ),
    },
    {
      title: '自定义资源',
      field: 'customResourceTypes',
      render: (row) => (
        row.hasCustomResources ? (
          <Box display="flex" style={{ gap: 4, flexWrap: 'wrap' }}>
            {row.customResourceTypes.slice(0, 3).map(type => (
              <Chip key={type} label={type} size="small" />
            ))}
            {row.customResourceTypes.length > 3 && (
              <Chip label={`+${row.customResourceTypes.length - 3}`} size="small" />
            )}
          </Box>
        ) : '-'
      ),
    },
    {
      title: '连通性',
      render: (row) => {
        const conn = connectivity[row.name];

        // 正在批量检测中且还没结果
        if (connectivityLoading && !conn) {
          return (
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="textSecondary">检测中...</Typography>
            </Box>
          );
        }

        if (conn?.status === 'connected') {
          return (
            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
              <CheckCircleIcon style={{ fontSize: 16, color: '#4caf50' }} />
              <Typography variant="caption" style={{ color: '#4caf50', fontWeight: 500 }}>Successful</Typography>
            </Box>
          );
        }

        if (conn?.status === 'error') {
          return (
            <Tooltip title={conn.error || '连接失败'} placement="top">
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <ErrorIcon style={{ fontSize: 16, color: '#f44336' }} />
                <Typography variant="caption" style={{ color: '#f44336', fontWeight: 500 }}>Failed</Typography>
              </Box>
            </Tooltip>
          );
        }

        return (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <RemoveCircleOutlineIcon style={{ fontSize: 16, color: '#bbb' }} />
            <Typography variant="caption" color="textSecondary">Unknown</Typography>
          </Box>
        );
      },
    },
    {
      title: '操作',
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<OpenInBrowserIcon style={{ fontSize: 16 }} />}
          onClick={() => onViewDetail(row.name)}
          style={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', padding: '2px 10px' }}
        >
          查看详情
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Tooltip title="刷新">
          <IconButton size="small" onClick={onRefresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Table
        options={{
          search: false,
          paging: false,
          padding: 'dense',
          toolbar: false,
        }}
        columns={columns}
        data={clusters}
        isLoading={loading}
        emptyContent={
          <Box p={2} textAlign="center">
            <Typography color="textSecondary">
              暂无配置的集群，请在 app-config.yaml 中配置 kubernetes.clusterLocatorMethods
            </Typography>
          </Box>
        }
      />
    </Box>
  );
};
